﻿import TKCustomElementFactory from '@tk/utilities/tk.custom.element.factory';
import render from '@tk/utilities/tk.render';

type MaxShowingThumbnails = 'auto' | number;

export default class TKAssetGallery extends TKCustomElementFactory {
    preview?: HTMLElement;
    thumbnails?: HTMLElement;
    overlayClassName: string;
    overlay: HTMLElement;
    maxShowingThumbnails: MaxShowingThumbnails;
    moreButtonClassName: string;
    selectedItemClassName: string;
    currentPosition: number = 0;
    thumbnailCount: number = 0;
    thumbnailShowingCount: number = 0;

    protected fullscreenPreview?: HTMLElement;
    protected fullscreenThumbnails?: HTMLElement;
    protected closeButton?: HTMLElement;
    protected isFullscreen: boolean = false;
    protected moreButton?: HTMLElement;
    protected shortCutHandler: Record<string, () => void>;
    protected resizeObserver: ResizeObserver;

    constructor() {
        super();

        this.preview = this.querySelector('[data-tk-asset-preview]') || undefined;
        this.thumbnails = this.querySelector('[data-tk-asset-thumbnails]') || undefined;
        this.overlayClassName =
            this.getAttribute('data-tk-overlay-class-name') || 'tk-asset-gallery tk-asset-gallery--fullscreen';
        this.overlay = render(`<div class="${this.overlayClassName}" hidden></div>`);
        this.maxShowingThumbnails = Number(this.getAttribute('data-tk-max-showing-thumbnails')) || 'auto';
        this.moreButtonClassName =
            this.getAttribute('data-tk-more-button-class-name') || 'tk-asset-gallery-item tk-asset-gallery-item--more';
        this.selectedItemClassName =
            this.getAttribute('data-tk-selected-item-class-name') || 'tk-asset-gallery-item--selected';
        this.shortCutHandler = {
            ArrowRight: () => this.jumpToPosition(this.currentPosition + 1),
            ArrowLeft: () => this.jumpToPosition(this.currentPosition - 1),
            Escape: () => this.toggleFullscreen(),
        };
        this.resizeObserver = new ResizeObserver(this.checkResize.bind(this));
    }

    connectedCallback(): void {
        if (!this.preview || !this.thumbnails) throw new Error('Asset Gallery: Elements are missing!');
        this.thumbnailCount = this.thumbnails.childElementCount;

        this.initFullscreen();
        this.resizeObserver.observe(this.thumbnails);
        this.registerClickListener();
        this.registerKeydownListener();
    }

    checkResize(entries: ResizeObserverEntry[]) {
        entries.forEach(() => {
            this.checkThumbnailLength();
        });
    }

    getFilteredThumbnails(): HTMLElement[] {
        if (!this.thumbnails) return [];
        return Array.from(this.thumbnails.children).filter((element) => element !== this.moreButton) as HTMLElement[];
    }

    checkThumbnailLength() {
        const childrenList = this.getFilteredThumbnails();
        childrenList.forEach((element) => {
            element.hidden = false;
        });
        if (this.maxShowingThumbnails === 'auto') this.optimizeThumbnails();
        else {
            if (this.thumbnailCount <= this.maxShowingThumbnails) return;
            this.trimThumbnailsToMaxCount(childrenList, this.maxShowingThumbnails);
        }
    }

    trimThumbnailsToMaxCount(childrenList: HTMLElement[], maxShowingThumbnails: number) {
        const difference = this.thumbnailCount - maxShowingThumbnails;
        const lastChildren = childrenList.slice(difference * -1);
        lastChildren.forEach((element) => {
            element.hidden = true;
        });
        if (!this.moreButton) this.createMoreButton(difference);
        else this.moreButton.setAttribute('data-count', `+${difference}`);
    }

    createMoreButton(difference: number) {
        this.moreButton = render(`<div class="${this.moreButtonClassName}" data-count="+${difference}"></div>`);
        this.thumbnails!.insertAdjacentElement('beforeend', this.moreButton);
        const handleExpandClick = this.expandGallery.bind(this);
        this.pushListener({ event: 'click', element: this.moreButton, action: handleExpandClick });
    }

    optimizeThumbnails() {
        this.moreButton && (this.moreButton.hidden = true);
        const { scrollWidth } = this.thumbnails!;
        const { width, right } = this.thumbnails!.getBoundingClientRect();
        if (width >= scrollWidth) return;
        const childrenList = this.getFilteredThumbnails();
        const closestHiddenChild = childrenList.find((child) => {
            const childRect = child.getBoundingClientRect();
            const childRight = childRect.right;
            return right < childRight;
        });
        if (!closestHiddenChild) return;
        const index = childrenList.indexOf(closestHiddenChild);
        this.thumbnailShowingCount = index - 1;
        this.trimThumbnailsToMaxCount(childrenList, this.thumbnailShowingCount);
        this.moreButton && (this.moreButton.hidden = false);
    }

    registerClickListener(): void {
        const handleThumbnailClick = this.jumpTo.bind(this);
        this.pushListener({ event: 'click', element: this.thumbnails!, action: handleThumbnailClick });
        this.fullscreenThumbnails &&
            this.pushListener({
                event: 'click',
                element: this.fullscreenThumbnails,
                action: handleThumbnailClick,
            });

        const handlePreviewClick = this.toggleFullscreen.bind(this);
        this.pushListener({ event: 'click', element: this.preview!, action: handlePreviewClick });
        this.closeButton &&
            this.pushListener({
                event: 'click',
                element: this.closeButton,
                action: handlePreviewClick,
            });
    }

    registerKeydownListener() {
        const handleShortcuts = this.initShortcuts.bind(this);
        this.pushListener({ event: 'keydown', element: document, action: handleShortcuts });
    }

    expandGallery() {
        this.currentPosition = this.thumbnailShowingCount;
        this.toggleFullscreen();
    }

    initShortcuts(event: KeyboardEvent) {
        if (!this.isFullscreen) return;
        if (Object.keys(this.shortCutHandler).every((key) => key !== event.key)) return;
        this.shortCutHandler[event.key]();
    }

    jumpTo(event: Event) {
        const target = event.target as HTMLElement;
        if (target === this.moreButton) return;
        const { parentElement } = target;
        if (!target || !parentElement) return;
        this.isFullscreen
            ? this.setCurrentPosition(parentElement, this.fullscreenThumbnails!)
            : this.setCurrentPosition(parentElement, this.thumbnails!);
        this.selectItem(this.thumbnails!, this.preview!);
        this.selectItem(this.fullscreenThumbnails!, this.fullscreenPreview!);
    }

    jumpToPosition(position: number) {
        this.currentPosition = this.getValidPosition(position);
        this.selectItem(this.thumbnails!, this.preview!);
        this.selectItem(this.fullscreenThumbnails!, this.fullscreenPreview!);
    }

    getValidPosition(position: number) {
        if (this.thumbnailCount <= 0) return 0;
        return ((position % this.thumbnailCount) + this.thumbnailCount) % this.thumbnailCount;
    }

    setCurrentPosition(parentElement: Element, thumbnails: HTMLElement) {
        const childList = Array.from(thumbnails.children);
        const index = childList.indexOf(parentElement);
        this.currentPosition = index;
    }

    selectItem(thumbnails: HTMLElement, preview: HTMLElement) {
        this.resetSelectedClassName(thumbnails);
        const child = thumbnails.children.item(this.currentPosition);
        child?.classList.add(this.selectedItemClassName);
        const previewImage = preview.children.item(this.currentPosition);
        previewImage?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
        const videoURL = previewImage?.getAttribute('data-tk-video-url');
        if (this.isFullscreen && videoURL && this.fullscreenPreview) {
            const videoChild = this.fullscreenPreview.children.item(this.currentPosition);
            const hasIframe = videoChild?.querySelector('iframe');
            if (hasIframe) return;
            if (!videoChild) return;
            const iframe = this.createIframe(videoURL);
            videoChild.innerHTML = '';
            videoChild.insertAdjacentElement('beforeend', iframe);
        }
    }

    resetSelectedClassName(element: Element) {
        const childList = Array.from(element.children);
        childList.forEach((child) => {
            child.classList.remove(this.selectedItemClassName);
        });
    }

    initFullscreen() {
        this.overlay.insertAdjacentHTML('afterbegin', this.innerHTML);
        document.body.insertAdjacentElement('beforeend', this.overlay);
        this.fullscreenThumbnails = this.overlay.querySelector('[data-tk-asset-thumbnails]') || undefined;
        this.fullscreenPreview = this.overlay.querySelector('[data-tk-asset-preview]') || undefined;
        this.closeButton = this.overlay.querySelector('[data-tk-close-fullscreen]') || undefined;
    }

    toggleFullscreen() {
        this.isFullscreen = !this.isFullscreen;
        this.overlay.hidden = !this.isFullscreen;
        if (this.closeButton) this.closeButton.hidden = !this.isFullscreen;

        if (this.isFullscreen) {
            this.selectItem(this.fullscreenThumbnails!, this.fullscreenPreview!);
        } else {
            this.selectItem(this.thumbnails!, this.preview!);
            this.stopAllVideos();
        }
    }

    stopAllVideos() {
        const iframe = this.overlay.querySelectorAll('iframe');
        iframe.forEach((video) => {
            video.contentWindow?.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo', args: '' }), '*');
        });
    }

    createIframe(videoURL: string): HTMLIFrameElement {
        const iframeTemplate = this.querySelector<HTMLTemplateElement>('[data-tk-iframe-template]');
        const clone = iframeTemplate?.content.cloneNode(true) as HTMLElement;
        const iframe = clone.querySelector<HTMLIFrameElement>('iframe');
        if (!iframe) throw new Error('Asset Gallery: iFrame Template is missing!');
        const enabledApiURL = new URL(videoURL);
        enabledApiURL.searchParams.set('enablejsapi', '1');
        iframe.src = enabledApiURL.href;
        return iframe;
    }
}
